<template>
  <section class="atlas-page">
    <div id="kendou-portal" class="atlas-shell">
      <header class="atlas-hero">
        <div class="hero-copy-zone">
          <p class="eyebrow">{{ t("eyebrow") }}</p>
          <h1 class="hero-title">{{ t("title") }}</h1>
          <p class="hero-subtitle">{{ t("lead") }}</p>
        </div>

        <div class="lang-pill">
          <button
            v-for="option in languages"
            :key="option"
            type="button"
            :class="['lang-chip', { active: option === lang }]"
            @click="setLang(option)"
          >
            {{ option.toUpperCase() }}
          </button>
        </div>
      </header>

      <section v-if="showPortalBoard" class="portal-board">
        <article class="portal-story panel-card">
          <p class="panel-tag">{{ t("portalTag") }}</p>
          <h2 class="panel-title">{{ loading ? t("loadingTitle") : t("portalTitle") }}</h2>
          <p class="panel-text">
            {{ loading ? t("loadingLead") : t("portalLead") }}
          </p>
        </article>

        <div class="portal-stats">
          <article class="stat-tile">
            <span class="stat-kicker">{{ t("series") }}</span>
            <strong class="stat-value">{{ loading ? '-' : portal.summary.seriesCount }}</strong>
          </article>
          <article class="stat-tile">
            <span class="stat-kicker">{{ t("topics") }}</span>
            <strong class="stat-value">{{ loading ? '-' : portal.summary.topicsCount }}</strong>
          </article>
          <article class="stat-tile">
            <span class="stat-kicker">{{ t("techniques") }}</span>
            <strong class="stat-value">{{ loading ? '-' : portal.summary.techniquesCount }}</strong>
          </article>
          <article class="stat-tile">
            <span class="stat-kicker">{{ t("terms") }}</span>
            <strong class="stat-value">{{ loading ? '-' : portal.summary.termsCount }}</strong>
          </article>
        </div>
      </section>

      <section v-if="showSeriesSection" id="kendou-series" class="strip-card panel-card">
        <div class="section-head compact-head">
          <div>
            <p class="panel-tag">{{ t("seriesTag") }}</p>
            <h2>{{ t("featuredSeries") }}</h2>
          </div>
          <span>{{ t("seriesHint") }}</span>
        </div>

        <div v-if="loading" class="placeholder-row">
          <div v-for="item in 2" :key="`series-skeleton-${item}`" class="ghost-card"></div>
        </div>
        <div v-else :class="['row-grid', 'two-up', { 'portal-scroll-frame': isPortalOverview, 'portal-scroll-frame--series': isPortalOverview }]">
          <article
            v-for="series in portal.featuredSeries"
            :key="series.seriesId"
            class="atlas-card clickable"
            @click="openSeries(series.slug)"
          >
            <div class="card-topline">
              <span class="soft-badge">{{ getCategoryLabel(series.seriesType, lang) }}</span>
              <span class="status-chip">{{ getStatusLabel(series.status, lang) }}</span>
            </div>
            <h3>{{ pickLangText(series.title, lang) }}</h3>
            <p>{{ summarizeText(series.summary, lang, 160) }}</p>
            <div class="meta-line">
              <span>{{ t("planned") }}: {{ series.plannedEpisodeCount ?? '-' }}</span>
              <span>{{ series.slug }}</span>
            </div>
          </article>
        </div>
      </section>

      <section v-if="showTopicsSection" id="kendou-topics" class="strip-card panel-card">
        <div class="section-head compact-head">
          <div>
            <p class="panel-tag">{{ t("topicsTag") }}</p>
            <h2>{{ t("latestTopics") }}</h2>
          </div>
          <span>{{ t("topicHint") }}</span>
        </div>

        <div v-if="loading" class="placeholder-row">
          <div v-for="item in 2" :key="`topic-skeleton-${item}`" class="ghost-card ghost-wide"></div>
        </div>
        <div v-else :class="['row-grid', 'two-up', { 'portal-scroll-frame': isPortalOverview, 'portal-scroll-frame--topics': isPortalOverview }]">
          <article
            v-for="topic in portal.latestTopics"
            :key="topic.topicId"
            class="atlas-card clickable"
            @click="openTopic(topic.slug)"
          >
            <div class="card-topline">
              <span class="soft-badge">{{ topic.episodeNo ? `EP ${topic.episodeNo}` : getCategoryLabel(topic.topicType, lang) }}</span>
              <span class="status-chip">{{ getStatusLabel(topic.status, lang) }}</span>
            </div>
            <h3>{{ pickLangText(topic.title, lang) }}</h3>
            <p>{{ summarizeText(topic.summary, lang, 170) }}</p>
            <div class="meta-line">
              <span>{{ topic.seriesTitle ? pickLangText(topic.seriesTitle, lang) : t("independent") }}</span>
              <span>{{ formatDate(topic.publishedAt) }}</span>
            </div>
          </article>
        </div>
      </section>

      <section
        v-if="showKnowledgeStage"
        :class="['split-stage', { 'single-stage': currentSection !== 'portal' }]"
      >
        <article v-if="showTechniquesSection" id="kendou-techniques" class="panel-card ledger-card">
          <div class="section-head compact-head">
            <div>
              <p class="panel-tag">{{ t("techniquesTag") }}</p>
              <h2>{{ t("currentTechniques") }}</h2>
            </div>
            <span>{{ t("techniqueHint") }}</span>
          </div>

          <div v-if="loading" class="placeholder-stack">
            <div v-for="item in 3" :key="`tech-skeleton-${item}`" class="ghost-card ghost-short"></div>
          </div>
          <div v-else :class="['stack-list', { 'portal-scroll-frame': isPortalOverview, 'portal-scroll-frame--knowledge': isPortalOverview }]">
            <article
              v-for="technique in portal.currentTechniques"
              :key="technique.techniqueId"
              class="mini-card clickable"
              @click="openTechnique(technique.slug)"
            >
              <div class="card-topline">
                <span class="soft-badge">{{ getCategoryLabel(technique.category, lang) }}</span>
                <span class="status-chip">v{{ technique.currentVersionNo ?? 1 }}</span>
              </div>
              <h3>{{ pickLangText(technique.item, lang) }}</h3>
              <p>{{ t("effectiveDate") }}: {{ formatDateOnly(technique.effectiveDate) }}</p>
              <div class="chip-row">
                <span v-for="tag in technique.tags || []" :key="tag" class="ink-chip">{{ tag }}</span>
              </div>
            </article>
          </div>
        </article>

        <article v-if="showTermsSection" id="kendou-terms" class="panel-card glossary-card">
          <div class="section-head compact-head">
            <div>
              <p class="panel-tag">{{ t("termsTag") }}</p>
              <h2>{{ t("termExplorer") }}</h2>
            </div>
            <span>{{ t("termHint") }}</span>
          </div>

          <label class="search-shell">
            <span>{{ t("termSearchLabel") }}</span>
            <div class="search-row">
              <input
                v-model.trim="termKeyword"
                type="text"
                :placeholder="t('termPlaceholder')"
                @keyup.enter="searchTerms"
              />
              <button type="button" :disabled="termLoading" @click="searchTerms">
                {{ termLoading ? t("searching") : t("search") }}
              </button>
            </div>
          </label>

          <p v-if="termErrorMessage" class="inline-error">{{ termErrorMessage }}</p>

          <div v-if="loading" class="placeholder-stack">
            <div v-for="item in 2" :key="`term-skeleton-${item}`" class="ghost-card ghost-short"></div>
          </div>
          <div v-else :class="['stack-list', { 'portal-scroll-frame': isPortalOverview, 'portal-scroll-frame--terms': isPortalOverview }]">
            <article
              v-for="term in visibleTerms"
              :key="term.termId"
              class="mini-card term-card clickable"
              @click="openTerm(term.termId)"
            >
              <div class="card-topline">
                <span class="soft-badge">{{ term.term }}</span>
                <span class="status-chip">{{ term.katakana || term.romaji || term.py || '-' }}</span>
              </div>
              <h3>{{ pickLangText(term.label, lang) }}</h3>
              <p>{{ summarizeText(term.description, lang, 120) }}</p>
            </article>
          </div>
        </article>
      </section>

      <section v-if="errorMessage" class="error-panel">
        <p class="panel-tag error-tag">{{ t("errorTag") }}</p>
        <h3>{{ t("errorTitle") }}</h3>
        <p>{{ errorMessage }}</p>
      </section>
    </div>
  </section>
</template>

<script setup>
import axios from "axios";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  API_BASE,
  ensureApiArray,
  ensureApiObject,
  formatDate,
  formatDateOnly,
  getCategoryLabel,
  getStatusLabel,
  pickLangText,
  summarizeText,
} from "./portalShared";
import { useSiteLanguage } from "../../store/siteLanguage";
console.log("Kendou TerminologyPage loaded");
const router = useRouter();
const route = useRoute();
const { lang, languages, setLang } = useSiteLanguage();

const copy = {
  ja: {
    eyebrow: "ITKENSHI KENDOU ATLAS",
    title: "IT剣士 剣道修練ポータル",
    lead: "成長の記録、技術、トピック、術語を一つに束ねた、IT剣士の剣道修練アーカイブです。",
    portalTag: "PORTAL",
    portalTitle: "このサイトの見方",
    loadingTitle: "Loading...",
    portalLead: "ここでは成長記録、シリーズ、トピック、知識、術語を横断して読めます。左のメニューから入口を選び、気になる内容を少しずつ辿ってください。",
    loadingLead: "kendou schema からシリーズ、エピソード、技術、術語を取得しています。",
    series: "SERIES",
    topics: "TOPICS",
    techniques: "TECHNIQUES",
    terms: "TERMS",
    seriesTag: "SERIES CHRONICLE",
    topicsTag: "RECENT EPISODES",
    techniquesTag: "PRACTICE LEDGER",
    termsTag: "GLOSSARY RADAR",
    featuredSeries: "シリーズ",
    latestTopics: "トピック",
    currentTechniques: "知識",
    termExplorer: "剣道用語",
    seriesHint: "骨格を先に把握するためのシリーズ帯。",
    topicHint: "録音済み・下書き段階の単集もここから確認。",
    techniqueHint: "現在採用している技術版を一覧化。",
    termHint: "語彙・仮名・拼音から横断検索。",
    termSearchLabel: "Search terms",
    termPlaceholder: "剣道 / 日本刀 / 鎬 / samurai",
    searching: "検索中",
    search: "検索",
    planned: "予定集数",
    independent: "独立テーマ",
    effectiveDate: "反映日",
    searchError: "術語検索に失敗しました。",
    errorTag: "STATUS",
    errorTitle: "Unable to load data.",
    loadError: "KendouDictionaryServer が起動しているか、ConnectionStrings:Default が設定されているか確認してください。",
  },
  zh: {
    eyebrow: "ITKENSHI KENDOU ATLAS",
    title: "IT剑士剑道修炼门户",
    lead: "把成长记录、技术、话题与术语收在一起，作为 IT剑士 持续积累的剑道修炼前台。",
    portalTag: "PORTAL",
    portalTitle: "本站用法简介",
    loadingTitle: "Loading...",
    portalLead: "这里把成长记录、系列、话题、知识和术语整理在一起。可以先从左侧菜单进入，再顺着你感兴趣的内容慢慢展开。",
    loadingLead: "正在从 kendou schema 读取 series、episodes、techniques 和 terms。",
    series: "SERIES",
    topics: "TOPICS",
    techniques: "TECHNIQUES",
    terms: "TERMS",
    seriesTag: "SERIES CHRONICLE",
    topicsTag: "RECENT EPISODES",
    techniquesTag: "PRACTICE LEDGER",
    termsTag: "GLOSSARY RADAR",
    featuredSeries: "Series",
    latestTopics: "Topics",
    currentTechniques: "Techniques",
    termExplorer: "Terms",
    seriesHint: "先看系列骨架，再决定进哪一集。",
    topicHint: "录音完成但尚未发布的内容也能先在这里预览。",
    techniqueHint: "集中展示当前采用的技术版本。",
    termHint: "从术语、假名、罗马字、拼音切入。",
    termSearchLabel: "Search terms",
    termPlaceholder: "剑道 / 日本刀 / 鎬 / samurai",
    searching: "检索中",
    search: "检索",
    planned: "计划集数",
    independent: "独立主题",
    effectiveDate: "生效日期",
    searchError: "术语检索失败。",
    errorTag: "STATUS",
    errorTitle: "Unable to load data.",
    loadError: "请确认 KendouDictionaryServer 已启动，并且 ConnectionStrings:Default 已配置。",
  },
  en: {
    eyebrow: "ITKENSHI KENDOU ATLAS",
    title: "IT Kenshi Kendo Practice Portal",
    lead: "A shared front for growth logs, techniques, topics, and terminology from IT Kenshi's continuing Kendo journey.",
    portalTag: "PORTAL",
    portalTitle: "How to Use This Portal",
    loadingTitle: "Loading...",
    portalLead: "This portal gathers growth logs, series, topics, techniques, and terminology in one place. Start from the left menu and follow the parts you want to explore.",
    loadingLead: "Fetching series, episodes, techniques, and terms from the kendou schema.",
    series: "SERIES",
    topics: "TOPICS",
    techniques: "TECHNIQUES",
    terms: "TERMS",
    seriesTag: "SERIES CHRONICLE",
    topicsTag: "RECENT EPISODES",
    techniquesTag: "PRACTICE LEDGER",
    termsTag: "GLOSSARY RADAR",
    featuredSeries: "Series",
    latestTopics: "Topics",
    currentTechniques: "Techniques",
    termExplorer: "Terms",
    seriesHint: "Read the frame before diving into episodes.",
    topicHint: "Preview published and pre-publish topic pages here.",
    techniqueHint: "A ledger of the current teaching versions.",
    termHint: "Search across kendo, language, and history.",
    termSearchLabel: "Search terms",
    termPlaceholder: "kendo / katana / shinogi / samurai",
    searching: "Searching",
    search: "Search",
    planned: "Planned",
    independent: "Independent",
    effectiveDate: "Effective",
    searchError: "Failed to search terms.",
    errorTag: "STATUS",
    errorTitle: "Unable to load data.",
    loadError: "Check that KendouDictionaryServer is running and that ConnectionStrings:Default is configured.",
  },
};

function t(key) {
  return copy[lang.value]?.[key] || key;
}

const portal = ref({
  summary: { seriesCount: 0, topicsCount: 0, techniquesCount: 0, termsCount: 0 },
  featuredSeries: [],
  latestTopics: [],
  currentTechniques: [],
  highlightedTerms: [],
});
const loading = ref(true);
const errorMessage = ref("");
const termKeyword = ref("");
const searchedTerms = ref([]);
const termLoading = ref(false);
const termErrorMessage = ref("");

const visibleTerms = computed(() => {
  if (termKeyword.value) {
    return searchedTerms.value;
  }
  return portal.value.highlightedTerms || [];
});

const sectionMap = {
  portal: "kendou-portal",
  series: "kendou-series",
  topics: "kendou-topics",
  techniques: "kendou-techniques",
  terms: "kendou-terms",
};

const currentSection = computed(() => {
  const sectionKey = typeof route.query.section === "string" ? route.query.section : "portal";
  return sectionMap[sectionKey] ? sectionKey : "portal";
});

const isPortalOverview = computed(() => currentSection.value === "portal");
const showPortalBoard = computed(() => currentSection.value === "portal");
const showSeriesSection = computed(() => currentSection.value === "portal" || currentSection.value === "series");
const showTopicsSection = computed(() => currentSection.value === "portal" || currentSection.value === "topics");
const showTechniquesSection = computed(() => currentSection.value === "portal" || currentSection.value === "techniques");
const showTermsSection = computed(() => currentSection.value === "portal" || currentSection.value === "terms");
const showKnowledgeStage = computed(() => showTechniquesSection.value || showTermsSection.value);

async function syncViewportForSection(sectionKey) {
  const sectionId = sectionMap[sectionKey] || sectionMap.portal;
  await nextTick();
  window.scrollTo({ top: 0, behavior: "smooth" });
  document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

async function loadPortal() {
  loading.value = true;
  errorMessage.value = "";

  try {
    const response = await axios.get(`${API_BASE}/api/kendou/portal`, { timeout: 10000 });
    portal.value = ensureApiObject(response, "Kendou portal API");
  } catch (error) {
    console.error(error);
    errorMessage.value = error?.response?.status === 404 ? "No kendo data found." : error?.message || t("loadError");
  } finally {
    loading.value = false;
  }
}

async function searchTerms() {
  termErrorMessage.value = "";

  if (!termKeyword.value) {
    searchedTerms.value = [];
    return;
  }

  termLoading.value = true;
  try {
    const response = await axios.get(`${API_BASE}/api/kendou/terms/search`, {
      params: { keyword: termKeyword.value, limit: 24 },
      timeout: 10000,
    });
    searchedTerms.value = ensureApiArray(response, "Kendou term search API");
  } catch (error) {
    console.error(error);
    searchedTerms.value = [];
    termErrorMessage.value = error?.message || t("searchError");
  } finally {
    termLoading.value = false;
  }
}

function openSeries(slug) {
  router.push(`/KendouPage/series/${encodeURIComponent(slug)}`);
}

function openTopic(slug) {
  router.push(`/KendouPage/topic/${encodeURIComponent(slug)}`);
}

function openTechnique(slug) {
  router.push(`/KendouPage/technique/${encodeURIComponent(slug)}`);
}

function openTerm(termId) {
  router.push(`/KendouPage/term/${encodeURIComponent(termId)}`);
}

onMounted(async () => {
  await loadPortal();
  await syncViewportForSection(currentSection.value);
});

watch(
  () => route.query.section,
  async (section) => {
    await syncViewportForSection(typeof section === "string" ? section : "portal");
  }
);
</script>

<style scoped>
.atlas-page {
  --paper: rgba(251, 245, 235, 0.92);
  --paper-strong: rgba(255, 250, 242, 0.98);
  --ink: #2f2116;
  --ink-soft: #5d4b3d;
  --line: rgba(182, 148, 110, 0.34);
  --accent: #9c341d;
  --accent-deep: #6d210f;
  --glow: rgba(218, 175, 108, 0.3);
  min-height: 100vh;
  padding: 20px;
  background:
    radial-gradient(circle at 18% 12%, rgba(255, 255, 255, 0.7), transparent 30%),
    radial-gradient(circle at 85% 78%, rgba(235, 190, 116, 0.34), transparent 26%),
    radial-gradient(circle at 96% 18%, rgba(229, 208, 170, 0.58), transparent 28%),
    linear-gradient(135deg, #e7d8bf 0%, #efe6d3 20%, #f5eedf 48%, #eadab8 100%);
}

.atlas-shell {
  max-width: 1480px;
  margin: 0 auto;
  display: grid;
  gap: 100px;
}

#kendou-portal,
#kendou-series,
#kendou-topics,
#kendou-techniques,
#kendou-terms {
  scroll-margin-top: 22px;
}

.atlas-hero {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 28px;
  padding: 6px 8px 2px;
}

.hero-copy-zone {
  max-width: 880px;
}

.eyebrow,
.panel-tag {
  margin: 0 0 10px;
  font-family: "Cormorant Garamond", "Times New Roman", serif;
  font-size: 14px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--accent);
}

.hero-title {
  margin: 0;
  font-family: "Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", Georgia, serif;
  font-size: clamp(2.8rem, 5.4vw, 4.8rem);
  line-height: 1.08;
  font-weight: 700;
  color: var(--ink);
}

.hero-subtitle {
  margin: 18px 0 0;
  max-width: 760px;
  font-size: 1.05rem;
  line-height: 1.9;
  color: var(--ink-soft);
}

.lang-pill {
  display: inline-flex;
  gap: 8px;
  padding: 8px;
  border: 1px solid rgba(191, 169, 139, 0.72);
  border-radius: 999px;
  background: rgba(255, 250, 245, 0.8);
  box-shadow: 0 10px 24px rgba(106, 75, 41, 0.08);
}

.lang-chip {
  border: 1px solid transparent;
  border-radius: 999px;
  background: transparent;
  color: #604d3d;
  font-family: "Cormorant Garamond", Georgia, serif;
  font-size: 1rem;
  padding: 10px 16px;
  cursor: pointer;
  transition: transform 0.22s ease, box-shadow 0.22s ease, background-color 0.22s ease, color 0.22s ease, border-color 0.22s ease;
}

.lang-chip.active {
  background: linear-gradient(135deg, #b64926 0%, #8a2613 100%);
  color: #fff8f3;
  box-shadow: inset 0 0 0 1px rgba(69, 18, 9, 0.4);
}

.lang-chip:hover {
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 10px 20px rgba(115, 80, 43, 0.14);
}

.lang-chip:active {
  transform: translateY(0) scale(0.98);
}

.portal-board,
.split-stage {
  display: grid;
  gap: 18px;
  align-items: start;
}

.portal-board {
  grid-template-columns: 1.1fr 0.9fr;
}

.split-stage {
  grid-template-columns: 1fr 1fr;
}

.single-stage {
  grid-template-columns: 1fr;
}

.panel-card,
.error-panel {
  border: 1px solid var(--line);
  border-radius: 28px;
  background: linear-gradient(180deg, var(--paper-strong) 0%, var(--paper) 100%);
  box-shadow:
    0 22px 44px rgba(118, 87, 49, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.75);
}

.portal-story,
.strip-card,
.ledger-card,
.glossary-card,
.error-panel {
  padding: 18px 20px 20px;
}

.portal-story {
  display: grid;
  align-content: start;
  min-height: 180px;
}

.panel-title {
  margin: 2px 0 12px;
  font-family: "Cormorant Garamond", "Noto Serif JP", serif;
  font-size: clamp(2.1rem, 3.4vw, 3rem);
  line-height: 1;
  color: var(--ink);
}

.panel-text,
.meta-line,
.section-head span,
.search-shell span,
.inline-error,
.error-panel p {
  color: var(--ink-soft);
  line-height: 1.8;
}

.portal-stats {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  align-items: start;
}

.stat-tile,
.atlas-card,
.mini-card {
  border: 1px solid rgba(216, 180, 139, 0.58);
  border-radius: 20px;
  background: rgba(255, 248, 239, 0.82);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.65);
  white-space: pre-line;
  transition:
    transform 0.26s ease,
    opacity 0.26s ease,
    filter 0.26s ease,
    box-shadow 0.26s ease,
    border-color 0.26s ease;
}

.stat-tile {
  min-height: 116px;
  padding: 18px 16px;
  display: grid;
  align-content: space-between;
}

.stat-kicker {
  font-family: "Cormorant Garamond", serif;
  font-size: 0.95rem;
  letter-spacing: 0.14em;
  color: #6b4f38;
}

.stat-value {
  font-family: "Cormorant Garamond", "Noto Serif JP", serif;
  font-size: clamp(2rem, 3vw, 3rem);
  line-height: 1;
  color: var(--ink);
}

.section-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-end;
}

.compact-head {
  margin-bottom: 14px;
}

.section-head h2 {
  margin: 2px 0 0;
  font-family: "Cormorant Garamond", "Noto Serif JP", serif;
  font-size: clamp(1.9rem, 2.6vw, 2.6rem);
  line-height: 1.1;
  color: var(--ink);
}

.row-grid {
  display: grid;
  gap: 14px;
  align-items: stretch;
}

.portal-scroll-frame {
  overflow-y: auto;
  padding-right: 6px;
  scrollbar-width: thin;
  scrollbar-color: rgba(156, 52, 29, 0.42) rgba(233, 219, 198, 0.72);
}

.portal-scroll-frame--series,
.portal-scroll-frame--topics {
  max-height: 430px;
}

.portal-scroll-frame--knowledge,
.portal-scroll-frame--terms {
  max-height: 480px;
}

.portal-scroll-frame::-webkit-scrollbar {
  width: 8px;
}

.portal-scroll-frame::-webkit-scrollbar-track {
  background: rgba(233, 219, 198, 0.72);
  border-radius: 999px;
}

.portal-scroll-frame::-webkit-scrollbar-thumb {
  background: rgba(156, 52, 29, 0.42);
  border-radius: 999px;
}

.portal-scroll-frame::-webkit-scrollbar-thumb:hover {
  background: rgba(138, 38, 19, 0.62);
}

.two-up {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.atlas-card,
.mini-card {
  padding: 16px 18px 18px;
}

.atlas-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.clickable {
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.clickable:hover {
  transform: translateY(-2px);
  border-color: rgba(184, 94, 55, 0.65);
  box-shadow:
    0 16px 28px rgba(117, 78, 40, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.75);
}

.card-topline,
.meta-line,
.chip-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}

.soft-badge,
.status-chip,
.ink-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 6px 11px;
  font-size: 0.78rem;
  letter-spacing: 0.06em;
}

.soft-badge {
  background: rgba(235, 220, 201, 0.9);
  color: #6d5138;
}

.status-chip {
  background: linear-gradient(135deg, #442616 0%, #24130b 100%);
  color: #fff7f0;
}

.ink-chip {
  background: rgba(245, 233, 216, 0.95);
  color: #705940;
}

.atlas-card h3,
.mini-card h3,
.error-panel h3 {
  margin: 12px 0 8px;
  font-family: "Noto Serif JP", "Yu Mincho", Georgia, serif;
  font-size: 1.55rem;
  line-height: 1.25;
  color: var(--ink);
}

.atlas-card p,
.mini-card p,
.error-panel p {
  margin: 0;
}

.atlas-card .meta-line {
  margin-top: auto;
  padding-top: 14px;
}

.stack-list,
.placeholder-stack {
  display: grid;
  gap: 12px;
  align-items: start;
}

.search-shell {
  display: grid;
  gap: 8px;
  margin-bottom: 14px;
}

.search-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
}

.search-row input {
  min-height: 50px;
  border: 1px solid rgba(205, 182, 152, 0.8);
  border-radius: 16px;
  background: rgba(255, 252, 246, 0.95);
  padding: 0 16px;
  color: var(--ink);
}

.search-row button {
  min-width: 112px;
  border: 0;
  border-radius: 16px;
  background: linear-gradient(135deg, #a54222 0%, #7c2813 100%);
  color: #fff8f4;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.22s ease, box-shadow 0.22s ease, filter 0.22s ease;
}

.search-row button:hover {
  transform: translateY(-2px);
  filter: saturate(1.04);
  box-shadow: 0 12px 24px rgba(120, 40, 19, 0.22);
}

.search-row button:active {
  transform: translateY(0) scale(0.985);
}

.inline-error {
  margin: 0 0 10px;
  color: #9a3320;
}

.error-panel {
  background: linear-gradient(180deg, rgba(246, 226, 208, 0.92) 0%, rgba(241, 214, 191, 0.9) 100%);
}

.error-tag {
  color: #8f2e1b;
}

.placeholder-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.ghost-card {
  border-radius: 20px;
  border: 1px solid rgba(216, 188, 151, 0.45);
  background: linear-gradient(90deg, rgba(248, 239, 227, 0.76) 25%, rgba(255, 248, 240, 0.96) 50%, rgba(248, 239, 227, 0.76) 75%);
  background-size: 240% 100%;
  animation: shimmer 1.8s infinite linear;
}

.ghost-wide {
  min-height: 140px;
}

.ghost-short {
  min-height: 120px;
}

.portal-stats:hover .stat-tile,
.row-grid:hover .atlas-card,
.stack-list:hover .mini-card {
  opacity: 0.74;
  transform: translateY(5px) scale(0.992);
  filter: saturate(0.86);
}

.portal-stats:hover .stat-tile:hover,
.row-grid:hover .atlas-card:hover,
.stack-list:hover .mini-card:hover {
  opacity: 1;
  transform: translateY(-6px) scale(1.012);
  filter: saturate(1.04);
  border-color: rgba(184, 94, 55, 0.65);
  box-shadow:
    0 18px 30px rgba(117, 78, 40, 0.16),
    inset 0 1px 0 rgba(255, 255, 255, 0.75);
}

@keyframes shimmer {
  from {
    background-position: 200% 0;
  }
  to {
    background-position: -40% 0;
  }
}

@media (max-width: 1100px) {
  .portal-board,
  .split-stage,
  .two-up,
  .placeholder-row,
  .search-row {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  .atlas-page {
    padding: 14px;
  }

  .atlas-hero {
    flex-direction: column;
    align-items: stretch;
  }

  .lang-pill {
    align-self: flex-start;
  }

  .portal-stats {
    grid-template-columns: 1fr 1fr;
  }
}

@keyframes atlasFadeLift {
  from {
    opacity: 0;
    transform: translateY(28px) scale(0.985);
    filter: blur(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
    filter: blur(0);
  }
}

.atlas-hero,
.portal-board,
.strip-card,
.split-stage,
.error-panel {
  animation: atlasFadeLift 0.72s cubic-bezier(0.2, 0.72, 0.2, 1) both;
}

.portal-board { animation-delay: 0.08s; }
.strip-card:nth-of-type(1) { animation-delay: 0.14s; }
.strip-card:nth-of-type(2) { animation-delay: 0.2s; }
.split-stage { animation-delay: 0.26s; }
.error-panel { animation-delay: 0.3s; }

.stat-tile,
.atlas-card,
.mini-card {
  animation: atlasFadeLift 0.66s cubic-bezier(0.2, 0.72, 0.2, 1) both;
}

.stat-tile:nth-child(1) { animation-delay: 0.16s; }
.stat-tile:nth-child(2) { animation-delay: 0.22s; }
.stat-tile:nth-child(3) { animation-delay: 0.28s; }
.stat-tile:nth-child(4) { animation-delay: 0.34s; }

.row-grid .atlas-card:nth-child(1) { animation-delay: 0.18s; }
.row-grid .atlas-card:nth-child(2) { animation-delay: 0.24s; }
.row-grid .atlas-card:nth-child(3) { animation-delay: 0.3s; }
.row-grid .atlas-card:nth-child(4) { animation-delay: 0.36s; }

.stack-list .mini-card:nth-child(1) { animation-delay: 0.18s; }
.stack-list .mini-card:nth-child(2) { animation-delay: 0.24s; }
.stack-list .mini-card:nth-child(3) { animation-delay: 0.3s; }
.stack-list .mini-card:nth-child(4) { animation-delay: 0.36s; }
.stack-list .mini-card:nth-child(5) { animation-delay: 0.42s; }
.stack-list .mini-card:nth-child(6) { animation-delay: 0.48s; }

@media (prefers-reduced-motion: reduce) {
  .atlas-hero,
  .portal-board,
  .strip-card,
  .split-stage,
  .error-panel,
  .stat-tile,
  .atlas-card,
  .mini-card,
  .lang-chip,
  .search-row button {
    animation: none !important;
    transition: none !important;
  }
}
</style>

